/**
 * 수신 webhook 서명.
 *
 * Cloudflare Email Worker와 우리 서버 사이에만 쓰인다. 이 엔드포인트는 공개
 * 인터넷에 열려 있으므로, 서명이 없으면 누구나 "메일이 왔다"고 가짜 요청을
 * 밀어넣어 남의 채팅방에 글을 심을 수 있다.
 *
 * Web Crypto만 쓴다 — Workers와 Node 22 양쪽에서 같은 코드가 돈다.
 */

const ENCODER = new TextEncoder();

export const SIGNATURE_HEADER = "x-catmail-signature";
export const TIMESTAMP_HEADER = "x-catmail-timestamp";

/** 이 시간을 넘긴 요청은 거절한다. 가로챈 요청을 나중에 재생하는 걸 막는다. */
export const MAX_SIGNATURE_AGE_SECONDS = 300;

// 반환 타입을 적지 않는다. CryptoKey는 런타임마다 선언 위치가 달라(DOM /
// WebWorker / @types/node) 이름으로 참조하면 어딘가에서 반드시 깨진다.
async function hmacKey(secret: string) {
  return crypto.subtle.importKey(
    "raw",
    ENCODER.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
}

function toHex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * 바이트의 SHA-256을 hex로.
 *
 * 원본 .eml처럼 **글자가 아닌 것**을 서명할 때 쓴다. 바이트를 문자열로 바꿔
 * 서명하면 인코딩이 조금만 달라도 양쪽 값이 갈린다. 해시를 먼저 내고 그
 * 문자열을 서명하면 그 위험이 사라진다.
 */
export async function sha256Hex(bytes: ArrayBuffer | Uint8Array): Promise<string> {
  // ⚠️ **원본 버퍼를 원본의 구간으로** 잘라야 한다. Node의 Buffer는 큰 풀 위에
  // 얹혀 있어서 byteOffset이 0이 아니다. 복사본을 만든 뒤 원본의 offset으로
  // 자르면 엉뚱한 구간이 나오고, 서로 다른 본문이 같은 해시가 되어 위조가
  // 통과한다. 실제로 그렇게 짰다가 테스트에서 잡혔다.
  const input =
    bytes instanceof Uint8Array
      ? (bytes.buffer.slice(
          bytes.byteOffset,
          bytes.byteOffset + bytes.byteLength,
        ) as ArrayBuffer)
      : bytes;

  return toHex(await crypto.subtle.digest("SHA-256", input));
}

/**
 * 타임스탬프를 서명에 함께 넣는다. 본문만 서명하면 같은 본문을 몇 번이고
 * 다시 보낼 수 있어 메시지가 중복 저장된다.
 */
export async function signPayload(
  secret: string,
  timestamp: number,
  body: string,
): Promise<string> {
  const key = await hmacKey(secret);
  const signature = await crypto.subtle.sign(
    "HMAC",
    key,
    ENCODER.encode(`${timestamp}.${body}`),
  );
  return toHex(signature);
}

/** 길이와 내용이 같은지, 조기 반환 없이 비교한다(타이밍 공격 방지). */
function constantTimeEquals(a: string, b: string): boolean {
  if (a.length !== b.length) return false;

  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

export type VerifyResult =
  | { ok: true }
  | { ok: false; reason: "missing" | "stale" | "mismatch" };

export async function verifyPayload(input: {
  secret: string;
  signature: string | undefined;
  timestamp: string | undefined;
  body: string;
  /** 테스트에서 시각을 고정하기 위한 구멍. 기본은 지금. */
  now?: number;
}): Promise<VerifyResult> {
  if (!input.signature || !input.timestamp) {
    return { ok: false, reason: "missing" };
  }

  const timestamp = Number(input.timestamp);
  if (!Number.isFinite(timestamp)) {
    return { ok: false, reason: "missing" };
  }

  const now = input.now ?? Math.floor(Date.now() / 1000);
  if (Math.abs(now - timestamp) > MAX_SIGNATURE_AGE_SECONDS) {
    return { ok: false, reason: "stale" };
  }

  const expected = await signPayload(input.secret, timestamp, input.body);
  return constantTimeEquals(expected, input.signature)
    ? { ok: true }
    : { ok: false, reason: "mismatch" };
}
